import { useState, useCallback, useEffect } from 'react';
import { CloudinaryUtils } from '@/utils/AspectWeaveCloudinaryUtils';
import { CloudinaryOverlays } from '@/utils/AspectWeaveCloudinaryOverlays';
import { CropParams, ContainerInfo, ImagePosition } from '../types';

/**
 * Computes Cloudinary crop parameters given a zoomable image and its viewport.
 *
 * - The image is assumed to be scaled uniformly (same ratio in both axes).
 * - The image element is inside a container with overflow:hidden; users pan by translating the image.
 * - The `getBoundingClientRect()` call gives current size and position on screen, including any CSS transforms.
 *
 * @param {HTMLImageElement} imgEl The <img> element being viewed.
 * @param {HTMLElement} viewportEl The viewport (e.g. a div) that hides overflow.
 * @param {string} viewMode The current view mode (unused - kept for compatibility).
 * @param {string} aspectRatio The current aspect ratio (unused - kept for compatibility).
 * @returns {{x:number,y:number,w:number,h:number,cloudinary:string}}
 */
function computeCrop(imgEl: HTMLImageElement, viewportEl: HTMLElement, viewMode: string = 'single', aspectRatio: string = '16:9') {
  const vp = viewportEl.getBoundingClientRect();
  const im = imgEl.getBoundingClientRect();

  // Use per-axis scale to be safe even if non-uniform transforms slip in
  const scaleX = im.width  / imgEl.naturalWidth;
  const scaleY = im.height / imgEl.naturalHeight;

  // Intersection of image and viewport in screen/CSS pixels
  const left   = Math.max(vp.left,   im.left);
  const top    = Math.max(vp.top,    im.top);
  const right  = Math.min(vp.right,  im.right);
  const bottom = Math.min(vp.bottom, im.bottom);

  const visWpx = Math.max(0, right - left);
  const visHpx = Math.max(0, bottom - top);

  // Map intersection to original image coordinates
  let x = Math.floor((left - im.left) / scaleX);
  let y = Math.floor((top  - im.top)  / scaleY);
  let w = Math.round(visWpx / scaleX);
  let h = Math.round(visHpx / scaleY);

  // Clamp to image bounds (Cloudinary requires int pixels within natural size)
  x = Math.max(0, Math.min(x, imgEl.naturalWidth));
  y = Math.max(0, Math.min(y, imgEl.naturalHeight));
  w = Math.max(1, Math.min(w, imgEl.naturalWidth  - x));
  h = Math.max(1, Math.min(h, imgEl.naturalHeight - y));

  return {
    x,
    y,
    w,
    h,
    // Build the Cloudinary transformation string
    cloudinary: `c_crop,x_${x},y_${y},w_${w},h_${h}`
  };
}

export const useCropCalculation = (
  isOpen: boolean,
  primaryImage: string,
  secondaryImage: string,
  imageZooms: Record<number, number>,
  imagePositions: Record<number, { x: number; y: number }>,
  gridImageZooms: Record<string, number>,
  gridImagePositions: Record<string, { x: number; y: number }>,
  rectangles?: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>,
  viewMode: string = 'single',
  aspectRatio: string = '16:9'
) => {
  const [debugCropParams, setDebugCropParams] = useState<Record<number, CropParams | null>>({});
  const [debugContainerInfo, setDebugContainerInfo] = useState<Record<number, ContainerInfo>>({});
  const [debugInfo, setDebugInfo] = useState<Record<string, any>>({});

  // Helper function to apply rectangle overlays to cropped URLs
  const applyRectangleOverlay = useCallback((baseUrl: string | null, imageId: string): string | null => {
    
    if (!baseUrl || !rectangles) {
      return baseUrl;
    }
    
    // Handle both numeric and string keys in rectangles
    let imageRectangles: Array<{ x: number; y: number; width: number; height: number; color?: string }> = [];
    
    // Try direct lookup first
    if (rectangles[imageId]) {
      imageRectangles = rectangles[imageId];
    } else {
      // Convert numeric keys to string keys for lookup
      Object.entries(rectangles).forEach(([key, rects]) => {
        const numKey = parseInt(key);
        if (!isNaN(numKey)) {
          // Map numeric keys to string keys
          const stringKey = numKey === 0 ? 'primary1' : 'secondary1';
          if (stringKey === imageId) {
            imageRectangles = rects;
          }
        }
      });
    }
    
    if (imageRectangles.length === 0) {
      return baseUrl;
    }
    
    // Get the first (and only) rectangle for this image
    const rect = imageRectangles[0];
    if (!rect) {
      return baseUrl;
    }
    
    // Get the crop parameters for this image to adjust overlay coordinates
    const imageIndex = imageId.includes('primary') ? 0 : 1;
    const cropParams = debugCropParams[imageIndex];
    

    // Find the image container and element for this imageId
    const canvasContainer = document.querySelector('[data-image-container]') as HTMLElement;
    if (!canvasContainer) {
      console.error('Canvas container not found');
      return baseUrl;
    }

    const imageContainer = canvasContainer.querySelector(`[data-image-id="${imageId}"]`) as HTMLElement;
    if (!imageContainer) {
      console.error(`Image container for ${imageId} not found`);
      return baseUrl;
    }

    const imageElement = imageContainer.querySelector('img') as HTMLImageElement;
    if (!imageElement) {
      console.error(`Image element for ${imageId} not found`);
      return baseUrl;
    }

    // Get zoom and pan values for this image
    const cropImageIndex = imageId.includes('primary') ? 0 : 1;
    const zoom = imageZooms[cropImageIndex] || 1;
    const panX = imagePositions[cropImageIndex]?.x || 0;
    const panY = imagePositions[cropImageIndex]?.y || 0;

    // Convert rectangle from container coordinates to image coordinates
    const imageCoordParams = CloudinaryOverlays.convertDisplayToImageCoords(
      rect,
      imageElement,
      imageContainer,
      zoom,
      panX,
      panY
    );

    // Now adjust for crop area if we have crop parameters
    let overlayX = imageCoordParams.x;
    let overlayY = imageCoordParams.y;
    let overlayWidth = imageCoordParams.width;
    let overlayHeight = imageCoordParams.height;
    
    if (cropParams) {
      // Adjust overlay position relative to crop area
      overlayX = imageCoordParams.x - cropParams.x;
      overlayY = imageCoordParams.y - cropParams.y;
      
      // Don't clamp coordinates - let them be positioned correctly within crop area
      // Only ensure dimensions don't exceed crop boundaries
      if (overlayX >= 0 && overlayY >= 0) {
        const maxWidth = cropParams.width - overlayX;
        const maxHeight = cropParams.height - overlayY;
        overlayWidth = Math.min(overlayWidth, Math.max(0, maxWidth));
        overlayHeight = Math.min(overlayHeight, Math.max(0, maxHeight));
      }
      
    }
    
    // Convert rectangle to Cloudinary overlay parameters
    const overlayParams = {
      x: overlayX,
      y: overlayY,
      width: overlayWidth,
      height: overlayHeight,
      color: (rect.color || '#D3E472').replace('#', ''),
      opacity: 15
    };
    
    
    return CloudinaryOverlays.generateOverlayUrl(baseUrl, overlayParams);
  }, [rectangles, debugCropParams]);

  // Update debug crop parameters whenever any relevant state changes
  const updateDebugCropParams = useCallback(() => {
    if (!isOpen) return;
    
    try {
      const newCropParams: Record<number, CropParams | null> = {};
      const newDebugInfo: Record<string, any> = {};
      let containerInfo: ContainerInfo | null = null;
      
      // Find the canvas container element
      const canvasContainer = document.querySelector('[data-image-container]') as HTMLElement;
      if (!canvasContainer) {
        console.error('Could not find canvas container');
        return;
      }

      // Get all image containers with data-image-id
      const imageContainers = canvasContainer.querySelectorAll('[data-image-id]');
      
      if (!imageContainers || imageContainers.length === 0) {
        console.error('Could not find image containers with data-image-id');
        return;
      }

      // Process each image container to generate crop parameters
      imageContainers.forEach((container, index) => {
        const imageElement = container.querySelector('img') as HTMLImageElement;
        if (!imageElement || !imageElement.naturalWidth || !imageElement.naturalHeight) {
          return;
        }

        const imageId = container.getAttribute('data-image-id') as string;
        
        // Compute crop using the provided function
        const cropResult = computeCrop(imageElement, container as HTMLElement, viewMode, aspectRatio);
        
        // Store crop parameters
        newCropParams[index] = {
          x: cropResult.x,
          y: cropResult.y,
          width: cropResult.w,
          height: cropResult.h
        };
        
        // Store container info (use first valid container)
        if (!containerInfo) {
          const containerRect = (container as HTMLElement).getBoundingClientRect();
          containerInfo = {
            width: containerRect.width,
            height: containerRect.height,
            aspectRatio: `${containerRect.width}:${containerRect.height}`
          };
        }
        
        // Enhanced debug info
        const containerRect = (container as HTMLElement).getBoundingClientRect();
        newDebugInfo[imageId] = {
          viewportRect: {
            width: containerRect.width,
            height: containerRect.height,
            left: containerRect.left,
            top: containerRect.top
          },
          imageRect: {
            width: imageElement.getBoundingClientRect().width,
            height: imageElement.getBoundingClientRect().height,
            left: imageElement.getBoundingClientRect().left,
            top: imageElement.getBoundingClientRect().top
          },
          naturalSize: {
            width: imageElement.naturalWidth,
            height: imageElement.naturalHeight
          },
          scale: imageElement.getBoundingClientRect().width / imageElement.naturalWidth,
          offsetX: imageElement.getBoundingClientRect().left - containerRect.left,
          offsetY: imageElement.getBoundingClientRect().top - containerRect.top,
          cropResult,
          cloudinaryTransform: cropResult.cloudinary
        };
        
        console.log(`=== CROP DEBUG FOR ${imageId} ===`);
        console.log('Viewport Rect:', containerRect);
        console.log('Image Rect:', imageElement.getBoundingClientRect());
        console.log('Natural Size:', { width: imageElement.naturalWidth, height: imageElement.naturalHeight });
        console.log('Scale Factor:', imageElement.getBoundingClientRect().width / imageElement.naturalWidth);
        console.log('Offset X:', imageElement.getBoundingClientRect().left - containerRect.left);
        console.log('Offset Y:', imageElement.getBoundingClientRect().top - containerRect.top);
        console.log('Crop Result:', cropResult);
        console.log('Cloudinary Transform:', cropResult.cloudinary);
      });
      
      // Update state with new crop parameters
      setDebugCropParams(newCropParams);
      setDebugInfo(newDebugInfo);
      
      // Store the container info
      if (containerInfo) {
        setDebugContainerInfo({ 0: containerInfo });
      }
    } catch (error) {
      console.error('Error updating debug crop params:', error);
    }
  }, [isOpen, primaryImage, secondaryImage, imageZooms, imagePositions, gridImageZooms, gridImagePositions, viewMode, aspectRatio]);

  // Generate cropped URLs for both primary and secondary images
  const generateCroppedUrls = useCallback(async (): Promise<{
    primaryCroppedUrl: string | null;
    secondaryCroppedUrl: string | null;
  }> => {
    const result = {
      primaryCroppedUrl: null as string | null,
      secondaryCroppedUrl: null as string | null
    };

    try {
      // Find the canvas container element
      const canvasContainer = document.querySelector('[data-image-container]') as HTMLElement;
      if (!canvasContainer) {
        console.error('Could not find canvas container');
        return result;
      }

      // Helper function to generate cropped URL for a specific container
      const generateCroppedUrlForContainer = async (container: HTMLElement, imageId: string, imageUrl: string): Promise<string | null> => {
        const imageElement = container.querySelector('img') as HTMLImageElement;
        if (!imageElement || !imageElement.naturalWidth || !imageElement.naturalHeight) {
          return null;
        }

        try {
          // Use the computeCrop function to get crop parameters
          const cropResult = computeCrop(imageElement, container, viewMode, aspectRatio);
          
          // Generate the cropped URL using CloudinaryUtils
          const croppedUrl = CloudinaryUtils.generateCroppedUrlFromParams(imageUrl, {
            x: cropResult.x,
            y: cropResult.y,
            width: cropResult.w,
            height: cropResult.h
          });

          // Apply rectangle overlay if it exists
          return applyRectangleOverlay(croppedUrl, imageId);
        } catch (error) {
          console.error(`Error generating cropped URL for ${imageId}:`, error);
          return null;
        }
      };

      // Get all image containers with data-image-id
      const imageContainers = canvasContainer.querySelectorAll('[data-image-id]');
      
      if (!imageContainers || imageContainers.length === 0) {
        console.error('Could not find image containers with data-image-id');
        return result;
      }

      // Process primary images (primary1, primary2, primary3)
      const primaryImageIds = ['primary1', 'primary2', 'primary3'];
      for (const imageId of primaryImageIds) {
        const container = canvasContainer.querySelector(`[data-image-id="${imageId}"]`) as HTMLElement;
        if (container && CloudinaryUtils.isCloudinaryUrl(primaryImage)) {
          const croppedUrl = await generateCroppedUrlForContainer(container, imageId, primaryImage);
          if (croppedUrl) {
            result.primaryCroppedUrl = croppedUrl;
            break; // Use the first valid primary image found
          }
        }
      }

      // Process secondary images (secondary1, secondary2)
      const secondaryImageIds = ['secondary1', 'secondary2'];
      for (const imageId of secondaryImageIds) {
        const container = canvasContainer.querySelector(`[data-image-id="${imageId}"]`) as HTMLElement;
        if (container && CloudinaryUtils.isCloudinaryUrl(secondaryImage)) {
          const croppedUrl = await generateCroppedUrlForContainer(container, imageId, secondaryImage);
          if (croppedUrl) {
            result.secondaryCroppedUrl = croppedUrl;
            break; // Use the first valid secondary image found
          }
        }
      }

    } catch (error) {
      console.error('Error generating cropped URLs:', error);
    }

    return result;
  }, [primaryImage, secondaryImage, applyRectangleOverlay]);

  // Generate a single cropped URL for a specific image index
  const generateCroppedUrl = useCallback(async (imageIndex: number): Promise<string | null> => {
    const imageUrl = imageIndex === 0 ? primaryImage : secondaryImage;
    
    if (!CloudinaryUtils.isCloudinaryUrl(imageUrl)) {
      console.warn('URL is not a Cloudinary URL:', imageUrl);
      return null;
    }

    const cropParams = debugCropParams[imageIndex];
    if (!cropParams) {
      console.warn('No crop parameters found for image index:', imageIndex);
      return null;
    }

    // First generate the cropped URL
    let croppedUrl = CloudinaryUtils.generateCroppedUrlFromParams(imageUrl, cropParams);
    if (!croppedUrl) {
      return null;
    }

    // Find the correct rectangle key for this image
    let rectangleKey = `primary${imageIndex + 1}`;
    
    console.log(`=== RECTANGLE KEY MAPPING FOR IMAGE INDEX ${imageIndex} ===`);
    console.log('Default rectangle key:', rectangleKey);
    console.log('All available rectangles:', rectangles);
    
    // Check if rectangles exist under the default key
    if (rectangles && !rectangles[rectangleKey]) {
      console.log(`No rectangles found under ${rectangleKey}, searching all keys...`);
      // Search through all available rectangle keys to find one with actual rectangles
      const availableKeys = Object.keys(rectangles).filter(key => 
        rectangles[key] && rectangles[key].length > 0
      );
      console.log('Available rectangle keys:', availableKeys);
      
      // For primary image (imageIndex 0), look for primary keys
      if (imageIndex === 0) {
        const primaryKey = availableKeys.find(key => key.includes('primary'));
        if (primaryKey) {
          rectangleKey = primaryKey;
          console.log(`Found primary rectangle key: ${rectangleKey}`);
        } else {
          console.log('No primary rectangle key found');
        }
      }
      // For secondary image (imageIndex 1), look for secondary keys  
      else if (imageIndex === 1) {
        const secondaryKey = availableKeys.find(key => key.includes('secondary'));
        if (secondaryKey) {
          rectangleKey = secondaryKey;
          console.log(`Found secondary rectangle key: ${rectangleKey}`);
        } else {
          console.log('No secondary rectangle key found');
        }
      }
    } else if (rectangles && rectangles[rectangleKey]) {
      console.log(`Found rectangles under default key ${rectangleKey}:`, rectangles[rectangleKey]);
    }

    console.log(`=== GENERATING CROPPED URL FOR IMAGE INDEX ${imageIndex} ===`);
    console.log('Using rectangle key:', rectangleKey);
    console.log('Crop params:', cropParams);
    
    // Then apply rectangle overlays if any exist
    return applyRectangleOverlay(croppedUrl, rectangleKey);
  }, [primaryImage, secondaryImage, debugCropParams, applyRectangleOverlay, rectangles]);

  // Auto-update debug crop parameters when dependencies change
  useEffect(() => {
    if (isOpen) {
      // Add a small delay to ensure DOM is ready
      const timeoutId = setTimeout(updateDebugCropParams, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [updateDebugCropParams, isOpen, gridImageZooms, gridImagePositions]);

  return {
    debugCropParams,
    debugContainerInfo,
    debugInfo,
    updateDebugCropParams,
    generateCroppedUrls,
    generateCroppedUrl
  };
};